import React, { useEffect, useState } from "react";
import {
  Briefcase,
  User,
  Loader2,
  Calendar,
} from "lucide-react";
import tokenService from "../../services/token-service";
import apiUrls from "../../config/api-urls";

const EmployeeTasks = () => {
  const [tasks, setTasks] = useState([]);
  const [totals, setTotals] = useState({ totalReferralAmount: 0, totalTasks: 0 });
  const [loading, setLoading] = useState(true);
  const [paymentStatus, setPaymentStatus] = useState("completed");
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0, pages: 0 });

  const token = tokenService.getToken();

  useEffect(() => {
    setPagination((prev) => ({ ...prev, page: 1 }));
  }, [paymentStatus]);

  useEffect(() => {
    fetchReferralTasks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.page, paymentStatus]);

  const fetchReferralTasks = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(
        `${apiUrls.candidate.getReferralTasks}?paymentStatus=${paymentStatus}&page=${pagination.page}&limit=${pagination.limit}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch referral tasks: ${response.statusText}`);
      }

      const result = await response.json();

      if (result.success) {
        setTasks(result.data.tasks || []);
        setTotals(result.data.totals || { totalReferralAmount: 0, totalTasks: 0 });
        setPagination(result.data.pagination || { page: 1, limit: 10, total: 0, pages: 0 });
      } else {
        throw new Error(result.message || "Failed to fetch referral tasks");
      }
    } catch (error) {
      setError(error.message || "Failed to load referral tasks. Please try again.");
      setTasks([]);
      setTotals({ totalReferralAmount: 0, totalTasks: 0 });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="h-8 w-8 animate-spin text-[#EFA938]" />
          <p className="text-gray-600 dark:text-gray-400 text-sm font-medium">Loading your referral tasks...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white dark:bg-slate-800 border-b border-gray-200 dark:border-slate-700 px-6 py-4 shadow-sm rounded-lg">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">My Referral Tasks</h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              Track all your referral earnings and completed tasks
            </p>
          </div>
          <div className="bg-gradient-to-br from-[#EFA938] to-orange-500 rounded-xl p-4 shadow-lg">
            <p className="text-white/90 text-xs font-medium mb-1">Total Earnings</p>
            <p className="text-white text-2xl font-bold">
              PKR {totals.totalReferralAmount.toLocaleString()}
            </p>
            <p className="text-white/80 text-xs mt-1">{totals.totalTasks} Tasks Completed</p>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <p className="text-red-800 dark:text-red-200 text-sm font-medium">{error}</p>
        </div>
      )}

      {/* Tasks List */}
      <div className="rounded-2xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-sm overflow-hidden">
        <header className="px-6 py-4 border-b border-gray-100 dark:border-slate-700 bg-gray-50 dark:bg-slate-900 flex items-center gap-3">
          <Briefcase className="h-5 w-5 text-[#EFA938]" />
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Referral Task History</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {tasks.length} task{tasks.length !== 1 ? "s" : ""}
            </p>
          </div>
        </header>

        <div className="divide-y divide-gray-100 dark:divide-slate-700">
          {tasks.length > 0 ? (
            tasks.map((task) => {
              const mainServiceName = task.service?.mainServiceName || "Service";
              const subServicesText = Array.isArray(task.service?.subServices)
                ? task.service.subServices.join(", ")
                : "";
              const referralAmount = task.referralAmount || 0;
              const taskAmount = task.price?.finalPrice || task.price?.customerPaidAmount || 0;
              const customerName = task.customer?.name || "Customer";
              const handymanName = task.handyman?.name || "Handyman";
              const paymentStatusLabel = task.paymentStatus === "completed" ? "Completed" : task.paymentStatus;

              return (
                <article
                  key={task.paymentDivisionId}
                  className="px-6 py-5 border-l-4 border-[#EFA938]"
                >
                  <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
                    {/* Left Section - Task Info */}
                    <div className="flex-1 space-y-4">
                      {/* Service Name */}
                      <div>
                        <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                          {mainServiceName}
                          {subServicesText && (
                            <>
                              <span className="text-[#EFA938] mx-2">→</span>
                              <span className="text-gray-700 dark:text-gray-300">{subServicesText}</span>
                            </>
                          )}
                        </h3>
                      </div>

                      {/* Task Date */}
                      <div className="flex flex-wrap items-center gap-4 text-sm">
                        <div className="flex items-center gap-2">
                          <Calendar className="h-4 w-4 text-[#EFA938]" />
                          <span className="text-gray-700 dark:text-gray-300 font-medium">
                            {new Date(task.createdAt).toLocaleDateString("en-US", {
                              day: "numeric",
                              month: "short",
                              year: "numeric",
                            })}
                          </span>
                        </div>
                        <span className="px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                          {paymentStatusLabel}
                        </span>
                      </div>

                      {/* Customer & Handyman Info */}
                      <div className="flex flex-wrap items-center gap-4 text-sm">
                        <div className="flex items-center gap-2">
                          <User className="h-4 w-4 text-[#EFA938]" />
                          <span className="text-gray-700 dark:text-gray-300 font-medium">
                            Customer: {customerName}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <User className="h-4 w-4 text-blue-500" />
                          <span className="text-gray-700 dark:text-gray-300 font-medium">
                            Handyman: {handymanName}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Right Section - Earnings */}
                    <div className="lg:min-w-[280px]">
                      <div className="bg-gradient-to-br from-[#EFA938] to-orange-500 rounded-xl p-4 shadow-lg">
                        <div className="flex items-center gap-2 mb-3">
                          <h4 className="text-white font-bold text-sm uppercase tracking-wide">💰 Earnings</h4>
                        </div>
                        
                        {/* Referral Earned */}
                        <div className="bg-white/20 backdrop-blur-sm rounded-lg p-3 mb-2">
                          <p className="text-white/90 text-xs font-medium mb-1">Referral Earned</p>
                          <p className="text-white text-2xl font-bold">
                            PKR {referralAmount.toLocaleString()}
                          </p>
                        </div>

                        {/* Task Amount */}
                        <div className="bg-white/20 backdrop-blur-sm rounded-lg p-3">
                          <p className="text-white/90 text-xs font-medium mb-1">Task Amount</p>
                          <p className="text-white text-2xl font-bold">
                            PKR {taskAmount.toLocaleString()}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </article>
              );
            })
          ) : (
            <div className="px-6 py-12 text-center">
              <Briefcase className="h-12 w-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
              <p className="text-gray-500 dark:text-gray-400 font-medium">No referral tasks found</p>
              <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">
                Your completed referral tasks will appear here
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EmployeeTasks;